<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UCContractServiceFeesEditor',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    service: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getInitialSelectedItem = (...args) => {
    const [items] = args;
    const today = new Date();
    let orderedItems = _.orderBy(items, 'valid_from');
    const result = _.chain(orderedItems)
      .filter(
        (p) =>
          p.valid_from >= today && (p.valid_to == null || p.valid_to <= today)
      )
      .head()
      .value();
    if (!result && items.length) {
      return _.last(orderedItems);
    }
    return result;
  };
  const createCopy = (...args) => {
    const [source] = args;
    const copy = _.cloneDeep(source);
    for (let attr of ['id', 'etag', '$etag']) {
      delete copy[attr];
    }

    if (copy.links) {
      delete copy.links['self'];
    }

    copy.valid_from = $fn.value.today();
    copy.contract_id = props.service.contract.contract_id;
    copy.service_id = props.service.id;

    items.value.push(copy);
    selectedItem.value = copy;
    console.log('copy crated', copy, selectedItem.value);
  };

  const items = ref(null);
  const selectedItem = ref(null);
  const ux = ref([]);
  const b_6705964765214521_style = computed(() => ({
    minWidth: '300px',
  }));
  const b_3943151158818434_displayParent = computed(() => false);

  const $data = computed(() => ({
    items: items.value,
    selectedItem: selectedItem.value,
    ux: ux.value,
    b_6705964765214521_style: b_6705964765214521_style.value,
    b_3943151158818434_displayParent: b_3943151158818434_displayParent.value,
  }));

  const $$load_items = async () => {
    return props.service.fees ? props.service.fees.slice() : [];
  };
  const $$load_selectedItem = async () => {
    return getInitialSelectedItem(items.value);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'selectedItem', dependsOn: ['items'] },
      { name: 'ux' },
      { name: 'b_6705964765214521_style', nodeId: '6705964765214521' },
      { name: 'b_3943151158818434_displayParent', nodeId: '3943151158818434' },
    ],
    loaders: { items: $$load_items, selectedItem: $$load_selectedItem },
    ux: {},
    dataRefs: {
      items,
      selectedItem,
      ux,
      b_6705964765214521_style,
      b_3943151158818434_displayParent,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const canCreateCopy = computed(() => {
    if (loaded.value) {
      return selectedItem.value && selectedItem.value.id != null;
    }
    return null;
  });

  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6806515264385235_script_u5q0xd6io_value_callable = async (
    $event
  ) => {
    return createCopy(selectedItem.value);
  };
  const act_6806515264385235_script_u5q0xd6io_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6806515264385235_script_u5q0xd6io_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_4ft8rco90 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_aje910de0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4ft8rco90,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_aje910de0],
    event: 'reload',
  });
  const act_6806515264385235_script_u5q0xd6io = new actionTypes.ScriptAction({
    actionArgs: act_6806515264385235_script_u5q0xd6io_getActionArgs,
  });
  const inst_evh_6806515264385235_clickPreventStop_oujmrfe4x =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_script_u5q0xd6io,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_6806515264385235_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6806515264385235_clickPreventStop_oujmrfe4x],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_aje910de0,
    inst_evh_7315092382398562_reload,
    inst_evh_6806515264385235_clickPreventStop_oujmrfe4x,
    inst_evh_6806515264385235_clickPreventStop,
  };

  const evh_7315092382398562_reload_aje910de0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_aje910de0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_clickPreventStop_oujmrfe4x = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6806515264385235_clickPreventStop_oujmrfe4x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card">
      <div class="card-header d-flex align-items-center">
        <span>
          {{ pgettext('ContractService', 'Fees') }}
        </span>
        <span class="badge bg-secondary ms-2" v-if="items && items.length">
          {{ items.length }}
        </span>
        <ItpSelect2
          name="selectedFee"
          v-model="selectedItem"
          :value="selectedItem"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (selectedItem =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          bind-object
          :options="items"
          use-option-template-for-selected-option
          optionTemplate='<UcContractServiceFeeCard :item="option"></UcContractServiceFeeCard>'
          class="ms-3"
          v-if="loaded"
          :style="b_6705964765214521_style"
        >
        </ItpSelect2>
        <ItpButton
          :text="pgettext('Button|Command', 'Create copy')"
          :tooltip="
            pgettext(
              'ContractServiceFeesEditor|CommandTooltip',
              'Copy the selected service fee.'
            )
          "
          class="ms-3"
          v-if="canCreateCopy"
          @click.prevent.stop="
            evh_6806515264385235_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </div>
      <div class="card-content">
        <ContractServiceFee
          :oid="selectedItem.id"
          :resource="selectedItem"
          no-header
          no-footer
          :displayParent="b_3943151158818434_displayParent"
          inline-commands
          v-if="selectedItem"
        >
        </ContractServiceFee>
      </div>
    </div>
  </ItpUsercontrol>
</template>
